import { Link } from "../shared/SiteLink";
import { ChevronLeft } from "lucide-react";
import { layout as brandingLayout } from "../../siteConfig";
import { layout as storeLayout } from "../../storeSiteConfig";
import { useTemplateType, useRelativePath } from "./TemplateContext";
import { useContentEditingSafe } from "./ContentEditingContext";

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

/**
 * Breadcrumbs — automatically resolves user-edited page names.
 *
 * When a user renames a page via the nav editor, the new name is stored
 * under the content key `nav:${path}`. This component reads those keys
 * and uses the updated name if available, falling back to the static
 * label provided in props.
 *
 * The last breadcrumb item (current page) often has no `path` prop.
 * In that case, `useRelativePath()` is used to look up the content key.
 */
export function Breadcrumbs({ items }: BreadcrumbsProps) {
  const templateType = useTemplateType();
  const layout = templateType === "store" ? storeLayout : brandingLayout;
  const ctx = useContentEditingSafe();
  const getContent = ctx?.getContent;
  const relativePath = useRelativePath();

  /** Read the user-edited label for a path, falling back to the original */
  const resolveLabel = (label: string, path?: string): string => {
    if (!getContent || !path) return label;
    const override = getContent(`nav:${path}`, "");
    return override || label;
  };

  // Home label can also be overridden (nav:/ for the home page)
  const homeLabel = getContent
    ? getContent("nav:/", layout.homeBreadcrumbLabel) || layout.homeBreadcrumbLabel
    : layout.homeBreadcrumbLabel;

  return (
    <nav className="bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <ol className="flex items-center gap-1 text-sm text-gray-500 flex-wrap">
          <li>
            <Link to={layout.homePath} className="hover:text-zap transition-colors">
              {homeLabel}
            </Link>
          </li>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            // For the last item (current page) without a path, use relativePath
            const lookupPath = item.path || (isLast ? relativePath : undefined);
            const displayLabel = resolveLabel(item.label, lookupPath);

            return (
              <li key={index} className="flex items-center gap-1">
                <ChevronLeft className="w-3.5 h-3.5 text-gray-400" />
                {item.path ? (
                  <Link to={item.path} className="hover:text-zap transition-colors">
                    {displayLabel}
                  </Link>
                ) : (
                  <span className="text-zap-navy" style={{ fontWeight: 500 }}>
                    {displayLabel}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
